import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useRedirectToProposal } from '../useCreateAgreement';
import { mapAgreementToDraft } from './mapAgreementToDraft';
/**
 * Copies an existing relationship (agreement or proposal) into a new standalone draft.
 *
 * The resulting draft is intentionally NOT linked back to its source relationship — it represents
 * a fresh proposal flow. The "withdraw-to-edit" lifecycle (cancel/withdraw the source proposal,
 * link the new draft) is handled by the BE `WithdrawForEditing` endpoint, not this hook.
 */ export const useDuplicateRelationship = ()=>{
    const { api, fetchLegalTerms } = useStoreActions();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { openCreationModal } = useRedirectToProposal();
    const { CUSTOM_PAGES } = useFeatureFlags();
    /**
     * Fetches a list of legal-terms doc-IDs that exist in the library of the current business.
     * An agreement/draft/proposal can contain a legal-terms doc from the library or a custom
     * one that's not from the library.
     */ async function fetchLegalTermsDocIds() {
        const legalTerms = await fetchLegalTerms();
        if (legalTerms) {
            return legalTerms.map((doc)=>doc.id);
        } else {
            throw makeSentryIgnoredError('legalTermsError');
        }
    }
    async function loadAndGo(type, action) {
        let opts = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
        try {
            startLoader("duplicate-".concat(type));
            return Promise.resolve(await action());
        } catch (e) {
            if (opts.onError) {
                opts.onError(e);
            } else {
                showErrorToast("Failed to duplicate ".concat(type, ", please try creating a new one instead"));
            }
            return Promise.reject();
        } finally{
            stopLoader("duplicate-".concat(type));
        }
    }
    async function getRelationship(relationshipId) {
        return await api.relationship.get(relationshipId);
    }
    async function duplicateRelationshipAndRedirect(relationshipId) {
        let strategy = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 'duplicate-agreement';
        openCreationModal({
            draftIdGetter: async ()=>{
                return loadAndGo('agreement', async ()=>{
                    const [currentAgreement, legalTermsDocIds] = await Promise.all([
                        getRelationship(relationshipId),
                        fetchLegalTermsDocIds()
                    ]);
                    if (currentAgreement != null) {
                        const draftData = mapAgreementToDraft(currentAgreement, {
                            strategy,
                            legalTermsDocIds,
                            customPagesFFEnabled: !!CUSTOM_PAGES
                        });
                        await api.draft.save(draftData);
                        return draftData.id;
                    } else {
                        return Promise.reject('error fetching agreement');
                    }
                });
            }
        });
        // openCreationModal handles the redirect; preserve the void return for the modal path.
        return Promise.resolve();
    }
    const createStandaloneDraftFromAgreement = async function createStandaloneDraftFromAgreement(relationshipId) {
        let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        const { strategy = 'duplicate-agreement' } = opts;
        const [currentAgreement, legalTermsDocIds] = await Promise.all([
            getRelationship(relationshipId),
            fetchLegalTermsDocIds()
        ]);
        const draftData = mapAgreementToDraft(currentAgreement, {
            strategy,
            legalTermsDocIds,
            customPagesFFEnabled: !!CUSTOM_PAGES
        });
        await api.draft.save(draftData);
        return draftData.id;
    };
    async function duplicateRelationship(relationshipId) {
        let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return loadAndGo('agreement', async ()=>createStandaloneDraftFromAgreement(relationshipId, opts), opts);
    }
    async function duplicateDraftFromId(draftId) {
        openCreationModal({
            draftIdGetter: async ()=>{
                return loadAndGo('draft', async ()=>{
                    const res = await api.draft.getById(draftId);
                    const id = generateUniqueId('draft');
                    const draftToSave = res.setEffectiveDate(null).setId(id).toDTO();
                    await api.draft.save(draftToSave);
                    return id;
                });
            }
        });
    }
    return {
        duplicateRelationshipAndRedirect,
        duplicateDraftFromId,
        duplicateRelationship,
        createStandaloneDraftFromAgreement
    };
};
